import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
// Extensie is verplicht: package.json staat op "type": "module", dus .js-bestanden
// worden als strikte ESM behandeld.
import { db, functions } from '../firebase.js';
import { getTranslatedStageName } from '../utils/stageHelpers.js';
import { parseDate } from '../utils/formatDate.js';

/**
 * Data-toegang voor de kalender: de rondes van een seizoen, de wedstrijden per
 * ronde en de spelersstatistieken per wedstrijd.
 *
 * De kalender staat in `schedule/{seasonId}/rounds/{roundKey}` met een
 * subcollectie `matches`, en per wedstrijd een subcollectie `playerStats`. Die
 * boom is voor iedereen leesbaar maar wordt alleen door Cloud Functions
 * geschreven — vandaar dat elke wijziging hieronder via een callable gaat.
 */

/** De statistieken die per speler per wedstrijd bestaan, in de volgorde van het scherm. */
export const STAT_FIELDS = [
    { key: 'isStarter', label: 'Basis', short: 'Basis', boolean: true },
    { key: 'minutes', label: 'Speelminuten', short: 'Min' },
    { key: 'goals', label: 'Doelpunten', short: 'Goals' },
    { key: 'ownGoals', label: 'Eigen doelpunten' },
    { key: 'assists', label: 'Assists', short: 'Assists' },
    { key: 'penaltiesScored', label: "Penalty's gescoord" },
    { key: 'penaltiesMissed', label: "Penalty's gemist" },
    { key: 'penaltiesSaved', label: "Penalty's gestopt" },
    { key: 'saves', label: 'Reddingen' },
    { key: 'aerielsWon', label: 'Gewonnen kopduels' },
    { key: 'tackles', label: 'Tackels' },
    { key: 'interceptions', label: 'Intercepties' },
    { key: 'accuratePasses', label: 'Accurate passes' },
    { key: 'yellow', label: 'Gele kaarten', short: 'Geel' },
    { key: 'red', label: 'Rode kaarten', short: 'Rood' },
];

/** De kolommen van de compacte spelerstabel op het wedstrijdscherm. */
export const COMPACT_STAT_FIELDS = STAT_FIELDS.filter((field) => field.short && field.key !== 'isStarter');

export const REVIEW_STATUS = {
    OPEN: 'open',
    APPROVED: 'approved',
};

const translatedPositions = {
    Goalkeeper: 'Keeper',
    Defender: 'Verdediger',
    Midfielder: 'Middenvelder',
    Attacker: 'Aanvaller',
    Coach: 'Trainer',
};

export const translatePosition = (position) => translatedPositions[position] || position || 'Onbekend';

const positionOrder = {
    Goalkeeper: 1, Defender: 2, Midfielder: 3, Attacker: 4, Coach: 5,
};

const roundsCollection = (seasonId) => collection(db, 'schedule', String(seasonId), 'rounds');
const matchesCollection = (seasonId, roundKey) => collection(
    db, 'schedule', String(seasonId), 'rounds', String(roundKey), 'matches',
);

/**
 * De sleutel waaronder de punten van een ronde in playerScores staan. Zie
 * roundScoreStorageId in functions/index.js: dit moet dezelfde uitkomst geven.
 */
export function roundScoreStorageId(round) {
    if (round.type === 'round' && round.roundId) return String(round.roundId);
    const key = String(round.id);
    return key.includes('-') ? key : `${key}-1`;
}

/** Leesbare naam van de ronde: de speelronde, of de fase plus welke wedstrijd. */
export function roundTitle(round) {
    if (round.roundName) return round.roundName;
    const stage = getTranslatedStageName(round.stageName) || 'Onbekende fase';
    return round.leg ? `${stage} · wedstrijd ${round.leg}` : stage;
}

/** Onderregel bij de rondenaam: de toernooifase waarin hij valt. */
export function roundSubtitle(round) {
    const stage = getTranslatedStageName(round.stageName);
    if (round.roundName) return stage || '';
    return round.leg ? 'Knockoutfase' : '';
}

/**
 * De stand van zaken van een ronde in drie stappen: gespeeld → opgehaald →
 * gecontroleerd. Het ontwerp kent er vier; vrijgeven aan de subleagues bestaat
 * nog niet en ontbreekt hier dus bewust.
 */
export function roundProgress(matches) {
    const now = Date.now();
    const started = matches.filter((m) => m.startingAt && m.startingAt.getTime() <= now);
    const synced = started.filter((m) => m.statsSyncedAt);
    const approved = started.filter((m) => m.reviewStatus === REVIEW_STATUS.APPROVED);
    const corrections = matches.reduce((total, m) => total + (m.correctionCount || 0), 0);

    let step = 0;
    let label = 'nog niet gespeeld';
    let nextAction = null;

    if (started.length > 0) {
        step = 1;
        label = `${started.length} van ${matches.length} gespeeld`;
        nextAction = 'sync';
    }
    if (started.length > 0 && synced.length === started.length) {
        step = 2;
        label = `${started.length} van ${matches.length} opgehaald`;
        nextAction = 'check';
    }
    if (started.length > 0 && approved.length === started.length) {
        step = 3;
        label = started.length === matches.length ? 'volledig akkoord' : `${approved.length} van ${started.length} akkoord`;
        nextAction = null;
    } else if (approved.length > 0) {
        label = `${approved.length} van ${started.length} akkoord`;
    }

    return {
        step,
        label,
        nextAction,
        matchCount: matches.length,
        startedCount: started.length,
        syncedCount: synced.length,
        approvedCount: approved.length,
        corrections,
    };
}

const toDate = parseDate;

function mapMatch(snapshot) {
    const data = snapshot.data();
    return {
        ...data,
        id: snapshot.id,
        fixtureId: snapshot.id,
        startingAt: toDate(data.starting_at),
        statsSyncedAt: toDate(data.statsSyncedAt),
        approvedAt: toDate(data.approvedAt),
        reviewStatus: data.reviewStatus || REVIEW_STATUS.OPEN,
        title: data.fixtureName || [data.homeTeamName, data.awayTeamName].filter(Boolean).join(' - '),
    };
}

/** Alle rondes van een seizoen, met hun wedstrijden. Nieuwste ronde eerst. */
export async function fetchCalendar(seasonId) {
    const roundsSnapshot = await getDocs(roundsCollection(seasonId));

    const rounds = await Promise.all(roundsSnapshot.docs.map(async (roundDoc) => {
        const matchesSnapshot = await getDocs(matchesCollection(seasonId, roundDoc.id));
        const matches = matchesSnapshot.docs
            .map(mapMatch)
            .sort((a, b) => (a.startingAt?.getTime() || 0) - (b.startingAt?.getTime() || 0));

        const round = { ...roundDoc.data(), id: roundDoc.id, matches };
        return {
            ...round,
            title: roundTitle(round),
            subtitle: roundSubtitle(round),
            firstKickoff: matches[0]?.startingAt || null,
            lastKickoff: matches[matches.length - 1]?.startingAt || null,
            progress: roundProgress(matches),
        };
    }));

    // Nieuwste ronde bovenaan: daar is de administrator mee bezig. Rondes zonder
    // wedstrijden (een lege import) zakken naar onderen.
    return rounds.sort((a, b) => (b.firstKickoff?.getTime() || 0) - (a.firstKickoff?.getTime() || 0));
}

/** Eén ronde met zijn wedstrijden. */
export async function fetchRound(seasonId, roundKey) {
    const roundSnapshot = await getDoc(doc(db, 'schedule', String(seasonId), 'rounds', String(roundKey)));
    if (!roundSnapshot.exists()) return null;

    const matchesSnapshot = await getDocs(matchesCollection(seasonId, roundKey));
    const matches = matchesSnapshot.docs
        .map(mapMatch)
        .sort((a, b) => (a.startingAt?.getTime() || 0) - (b.startingAt?.getTime() || 0));

    const round = { ...roundSnapshot.data(), id: roundSnapshot.id, matches };
    return {
        ...round,
        title: roundTitle(round),
        subtitle: roundSubtitle(round),
        firstKickoff: matches[0]?.startingAt || null,
        lastKickoff: matches[matches.length - 1]?.startingAt || null,
        progress: roundProgress(matches),
    };
}

/** Eén wedstrijd uit de kalender. */
export async function fetchMatch(seasonId, roundKey, fixtureId) {
    const snapshot = await getDoc(
        doc(db, 'schedule', String(seasonId), 'rounds', String(roundKey), 'matches', String(fixtureId)),
    );
    return snapshot.exists() ? mapMatch(snapshot) : null;
}

/**
 * De opgehaalde statistieken per speler van één wedstrijd, samen met de punten en
 * de puntenopbouw uit playerScores.
 *
 * Alleen spelers met speelminuten komen terug: wie niet in actie kwam heeft geen
 * cijfers om te controleren.
 */
export async function fetchMatchPlayerStats(seasonId, roundKey, round, fixtureId) {
    const storageId = roundScoreStorageId(round);

    const [statsSnapshot, scoresSnapshot] = await Promise.all([
        getDocs(collection(
            db, 'schedule', String(seasonId), 'rounds', String(roundKey), 'matches', String(fixtureId), 'playerStats',
        )),
        getDocs(query(
            collection(db, 'playerScores', String(seasonId), 'rounds', String(storageId), 'scores'),
            where('fixtureId', '==', Number(fixtureId)),
        )),
    ]);

    const scoreByPlayer = new Map();
    scoresSnapshot.docs.forEach((scoreDoc) => {
        const score = scoreDoc.data();
        scoreByPlayer.set(String(score.playerId), score);
    });

    return statsSnapshot.docs
        .map((statDoc) => {
            const data = statDoc.data();
            const score = scoreByPlayer.get(statDoc.id);
            return {
                ...data,
                playerId: statDoc.id,
                effective: data.effective || {},
                manual: data.manual || {},
                api: data.api || {},
                points: score?.totalPoints ?? null,
                pointsFromApi: score?.pointsFromApi ?? null,
                breakdown: score?.breakdown || {},
            };
        })
        .filter((player) => (player.effective.minutes || 0) > 0)
        .sort((a, b) => {
            const club = (a.clubName || '').localeCompare(b.clubName || '');
            if (club !== 0) return club;
            const position = (positionOrder[a.position] || 99) - (positionOrder[b.position] || 99);
            if (position !== 0) return position;
            return (a.playerName || '').localeCompare(b.playerName || '');
        });
}

/** De puntentelling, voor de labels bij de puntenopbouw. */
export async function fetchScoringRules() {
    const snapshot = await getDocs(collection(db, 'scoringRules'));
    return snapshot.docs.reduce((rules, ruleDoc) => {
        rules[ruleDoc.id] = ruleDoc.data();
        return rules;
    }, {});
}

/** Zet de puntenopbouw van een speler om in regels, in de volgorde van de puntentelling. */
export function breakdownRows(breakdown, scoringRules) {
    return Object.entries(breakdown || {})
        .map(([rule, data]) => ({
            rule,
            action: scoringRules[rule]?.action || rule,
            order: scoringRules[rule]?.order ?? 999,
            count: data.count,
            points: data.points,
        }))
        .sort((a, b) => a.order - b.order);
}

export const syncRoundStats = (seasonId, roundKey) => httpsCallable(functions, 'syncRoundStats')({
    seasonId, roundKey,
}).then((result) => result.data);

export const syncMatchStats = (seasonId, roundKey, fixtureId) => httpsCallable(functions, 'syncMatchStats')({
    seasonId, roundKey, fixtureId,
}).then((result) => result.data);

export const setPlayerStatCorrection = (payload) => httpsCallable(functions, 'setPlayerStatCorrection')(payload)
    .then((result) => result.data);

export const setMatchReviewStatus = (seasonId, roundKey, fixtureId, approved) => httpsCallable(
    functions, 'setMatchReviewStatus',
)({
    seasonId, roundKey, fixtureId, approved,
}).then((result) => result.data);

export const importSchedule = (seasonId) => httpsCallable(functions, 'importScheduleForSeason')({ seasonId })
    .then((result) => result.data);

export const copyFinalRoundsToAuctions = (seasonId) => httpsCallable(functions, 'copyFinalRoundsToAuctions')({ seasonId })
    .then((result) => result.data);
